import {
    FilledInput,
    FormControl,
    FormHelperText,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    OutlinedInput,
} from '@mui/material';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { FormikField } from './FormikFields';
import PropTypes from 'prop-types';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { shouldRetranslate } from '../../state/locale/Locale.selectors';
import startCase from 'lodash/startCase';
import { translate } from '../../helpers/i18n';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

/**
 * @typedef {Object} Base
 * @property {boolean=} allowShowHide Whether to allow the user to toggle the visibility of the password field
 * @property {Record<string,any>=} translationOptions The `options` object provided to all internal <Locale/> translations for the TextField
 *
 * @typedef {import('@mui/material').TextFieldProps & Base} TextFieldProps
 */

/**
 * @type {React.ForwardRefExoticComponent<TextFieldProps>}
 */
export const TextField = React.forwardRef((props, ref) => {
    const {
        allowShowHide,
        'aria-describedby': ariaDescribedBy,
        children,
        defaultValue,
        error,
        FormControlProps,
        FormHelperTextProps,
        fullWidth,
        helperText,
        id: providedId,
        InputLabelProps,
        InputProps,
        inputProps: { placeholder: providedPlaceholder, ...inputProps } = {},
        name,
        label = name ? startCase(`${name}`) : undefined,
        placeholder,
        required,
        translationOptions,
        value,
        variant,
        ...remain
    } = props;

    const id = useRef(providedId || uuid());
    const errorId = useRef(uuid());
    const updateTranslation = useSelector(shouldRetranslate);
    const translatedPlaceholder = useMemo(
        () => placeholder && translate(placeholder, translationOptions),
        [placeholder, updateTranslation, translationOptions]
    );

    const translatedLabel = useMemo(
        () =>
            !label || React.isValidElement(label) ? label : translate(label, translationOptions),
        [label, updateTranslation, translationOptions]
    );

    const [type, setType] = useState(props.type || 'text');
    const inputVariant = variant || 'outlined';
    const InputComponent = useMemo(() => {
        switch (inputVariant) {
            case 'filled':
                return FilledInput;
            case 'standard':
                return Input;
            default:
                return OutlinedInput;
        }
    }, [inputVariant]);

    const handleClickShowPassword = useCallback(() => {
        setType(prevType => (prevType === 'password' ? 'text' : 'password'));
    }, []);

    return (
        <FormControl
            fullWidth={!!fullWidth}
            error={error}
            required={!!required}
            {...FormControlProps}
        >
            <InputLabel
                shrink={placeholder || value ? true : (!label && providedPlaceholder) || undefined}
                htmlFor={id.current}
                {...InputLabelProps}
            >
                {translatedLabel}
            </InputLabel>
            <InputComponent
                id={id.current}
                inputRef={ref}
                name={name}
                label={translatedLabel}
                aria-describedby={
                    !helperText // If there is an error, the aria-describedby needs to reference the error message, and that's handled by MUI internally
                        ? ariaDescribedBy
                        : undefined
                }
                margin="dense"
                inputProps={inputProps}
                {...remain}
                type={type}
                notched={
                    (InputComponent === OutlinedInput && !!((placeholder || value) && label)) ||
                    undefined
                }
                {...(props.type === 'password' && allowShowHide
                    ? {
                          endAdornment: (
                              <InputAdornment position="end">
                                  <IconButton onClick={handleClickShowPassword} edge="end">
                                      {type === 'password' ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                              </InputAdornment>
                          ),
                      }
                    : undefined)}
                value={value ?? ''}
                {...InputProps}
                placeholder={translatedPlaceholder || providedPlaceholder}
                defaultValue={value != null ? undefined : defaultValue}
            >
                {children}
            </InputComponent>
            <FormHelperText
                {...FormHelperTextProps}
                id={errorId.current}
                sx={{ whiteSpace: 'pre-line', ...FormHelperTextProps?.sx }}
            >
                {helperText}
            </FormHelperText>
        </FormControl>
    );
});

TextField.propTypes = {
    allowShowHide: PropTypes.bool,
    'aria-describedby': PropTypes.string,
    children: PropTypes.node,
    defaultValue: PropTypes.any,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    FormControlProps: PropTypes.object,
    FormHelperTextProps: PropTypes.object,
    fullWidth: PropTypes.bool,
    helperText: PropTypes.any,
    id: PropTypes.string,
    InputLabelProps: PropTypes.object,
    InputProps: PropTypes.object,
    inputProps: PropTypes.object,
    label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    name: PropTypes.string,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    translationOptions: PropTypes.object,
    type: PropTypes.string,
    value: PropTypes.any,
    variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
};

export default TextField;

// Built inline rather than via withFormikField: calling a hoisted HOC from
// another module at module scope reads that module's `React` binding before
// its body has run whenever the bundler enters this import cycle from the
// other side. Dereferencing FormikField at render time avoids that entirely.
export const FormikTextField = React.forwardRef((props, ref) => (
    <FormikField {...props} component={TextField} ref={ref} />
));
FormikTextField.displayName = 'FormikTextField';
